import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Autocomplete,
  Box,
  Chip,
} from '@mui/material';

interface EmailTag {
  id: string;
  name: string;
  color: string;
}

interface EmailTagDialogProps {
  open: boolean;
  availableTags: EmailTag[];
  selectedTag: EmailTag | null;
  newTagName: string;
  loading: boolean;
  onClose: () => void;
  onAddExistingTag: (tagId: string) => void;
  onCreateAndAddTag: () => void;
  onTagNameChange: (name: string) => void;
}

export const EmailTagDialog: React.FC<EmailTagDialogProps> = ({
  open,
  availableTags,
  selectedTag,
  newTagName,
  loading,
  onClose,
  onAddExistingTag,
  onCreateAndAddTag,
  onTagNameChange,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add Email Tag</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select an existing tag or create a new one:
        </Typography>

        {/* Existing Tags Dropdown */}
        <Autocomplete
          options={availableTags}
          getOptionLabel={(option) => option.name}
          value={selectedTag}
          onChange={(event, newValue) => {
            if (newValue) {
              onAddExistingTag(newValue.id);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select a tag"
              placeholder="Choose from existing tags"
              size="small"
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={option.name}
                  size="small"
                  sx={{ backgroundColor: option.color, color: 'white' }}
                />
              </Box>
            );
          }}
          disabled={loading}
          sx={{ mb: 3 }}
        />

        {/* Create New Tag */}
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          Or create a new tag:
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter tag name"
          value={newTagName}
          onChange={(e) => onTagNameChange(e.target.value)}
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newTagName.trim()) {
              e.preventDefault();
              onCreateAndAddTag();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onCreateAndAddTag}
          variant="contained"
          disabled={!newTagName.trim() || loading}
        >
          {loading ? <CircularProgress size={20} /> : 'Create & Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
